import React from 'react';
import {
  RiBuildingLine,
  RiTeamLine,
  RiPercentLine,
  RiCalendarCheckLine,
} from 'react-icons/ri';
import type { CompanyForm, UserInfo } from '../../services/companyService';
import ValidatedInput from '../ui/ValidatedInput';
import ValidatedTextarea from '../ui/ValidatedTextarea';
import SearchableSelect from '../ui/SearchableSelect';
import { blockNonNumericKeys } from '../../utils/numberInput';

/**
 * ช่องกรอกของฟอร์มบริษัท — ย้ายจาก renderCompanyForm ของโมดัลใน CompanyPage (28 ต.ค. 2026)
 * มาใช้ในหน้าเต็ม /master-data/companies/new และ /:id/edit (CompanyFormPage) · ช่องครบเท่าเดิมทุกช่อง
 *
 * state อยู่ที่หน้าที่เรียก (controlled) · ตรรกะ validate/แปลงค่าอยู่ที่ ./companyForm.ts
 * ทุกช่องในนี้ "มีช่องกรอกจริง" → service ส่ง key ได้ (ล้างค่าได้) — ห้ามเพิ่ม key ใน CompanyForm ที่ไม่มีช่องที่นี่
 * (memory form-field-overwrite-bug-class: ไม่มีช่องกรอกห้ามส่ง key)
 */
export interface CompanyFormFieldsProps {
  formData: CompanyForm;
  setFormData: React.Dispatch<React.SetStateAction<CompanyForm>>;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  /** ข้อความเตือนรหัสซ้ำ (สร้างรหัสใหม่ให้แล้ว รอผู้ใช้กดบันทึกอีกครั้ง) */
  codeError: string;
  setCodeError: (msg: string) => void;
  /** รายชื่อพนักงานขาย (GET /users/sales-persons) */
  users: Array<{ id: number; full_name?: string; username: string }>;
  /** พนักงานขายเดิมของบริษัท — เผื่อไม่ได้อยู่ใต้ Role Sales แล้ว (ข้อมูลเก่า) ต้องยังแสดงชื่อ + ไม่ทำให้ค่าหาย */
  currentSalesPerson?: UserInfo | null;
}

const CompanyFormFields: React.FC<CompanyFormFieldsProps> = ({
  formData, setFormData, formErrors, setFormErrors, codeError, setCodeError, users, currentSalesPerson,
}) => {
  return (
    <div className="space-y-6">
      {/* Section: ข้อมูลพื้นฐาน */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
            <RiBuildingLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ข้อมูลพื้นฐานบริษัท</h3>
        </div>

        {/* Row 1: รหัสบริษัท และ ชื่อบริษัท */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ValidatedInput
              label="รหัสบริษัท"
              name="code"
              value={formData.code}
              onChange={(e) => {
                setFormData(prev => ({...prev, code: e.target.value}));
                if (formErrors.code) setFormErrors(prev => ({...prev, code: ''}));
                if (codeError) setCodeError('');
              }}
              required
              disabled={true}
              placeholder="รหัสจะสร้างอัตโนมัติ"
              error={codeError || formErrors.code}
            />

            <ValidatedInput
              label="ชื่อบริษัท"
              name="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({...prev, name: e.target.value}));
                if (formErrors.name) setFormErrors(prev => ({...prev, name: ''}));
              }}
              required
              placeholder="ใส่ชื่อบริษัท"
              error={formErrors.name}
            />
        </div>

        {/* Row 2: เลขผู้เสียภาษี และ อีเมล */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ValidatedInput
            label="เลขประจำตัวผู้เสียภาษี"
            name="taxId"
            value={formData.taxId}
            onChange={(e) => {
              setFormData(prev => ({...prev, taxId: e.target.value}));
              if (formErrors.taxId) setFormErrors(prev => ({...prev, taxId: ''}));
            }}
            placeholder="เลข 13 หลัก (ไม่บังคับ)"
            error={formErrors.taxId}
          />

          <ValidatedInput
            label="อีเมล"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => {
              setFormData(prev => ({...prev, email: e.target.value}));
              if (formErrors.email) setFormErrors(prev => ({...prev, email: ''}));
            }}
            placeholder="example@company.com"
            error={formErrors.email}
          />
        </div>

        {/* ที่อยู่ - full width */}
        <ValidatedTextarea
          label="ที่อยู่"
          name="address"
          value={formData.address}
          onChange={(e) => {
            setFormData(prev => ({...prev, address: e.target.value}));
            if (formErrors.address) setFormErrors(prev => ({...prev, address: ''}));
          }}
          required
          placeholder="ใส่ที่อยู่บริษัท"
          rows={2}
          error={formErrors.address}
        />
      </div>

      {/* Section: ข้อมูลติดต่อ */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
            <RiTeamLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ข้อมูลติดต่อ</h3>
        </div>

        {/* Row 3: ผู้ติดต่อ และ เบอร์โทร */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ValidatedInput
            label="ผู้ติดต่อ"
            name="contactPerson"
            value={formData.contactPerson}
            onChange={(e) => {
              setFormData(prev => ({...prev, contactPerson: e.target.value}));
              if (formErrors.contactPerson) setFormErrors(prev => ({...prev, contactPerson: ''}));
            }}
            placeholder="ชื่อผู้ติดต่อ"
            error={formErrors.contactPerson}
          />

          <ValidatedInput
            label="เบอร์โทรศัพท์"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => {
              setFormData(prev => ({...prev, phone: e.target.value}));
              if (formErrors.phone) setFormErrors(prev => ({...prev, phone: ''}));
            }}
            placeholder="0x-xxxx-xxxx"
            error={formErrors.phone}
          />
        </div>

        {/* Field ใหม่ - พนักงานขายและสถานะ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              พนักงานขายที่ดูแล
            </label>
            <SearchableSelect
              options={(() => {
                const opts = users.map(user => ({
                  value: user.id,
                  label: user.full_name || user.username
                }));
                // edit/view: เผื่อพนักงานขายเดิมของบริษัทไม่ได้อยู่ใต้ Role Sales (ข้อมูลเก่า)
                // → merge เข้าไปด้วยเพื่อให้ยังแสดงชื่อ + ไม่ทำให้ค่าที่บันทึกไว้หาย
                const sp = currentSalesPerson;
                if (sp && sp.id && !users.some(u => u.id === sp.id)) {
                  opts.push({ value: sp.id, label: sp.full_name || sp.username || `#${sp.id}` });
                }
                return opts;
              })()}
              value={formData.sales_person_id || ''}
              onChange={(value) => {
                setFormData(prev => ({...prev, sales_person_id: value ? Number(value) : undefined}));
              }}
              placeholder="-- เลือกพนักงานขาย --"
              searchPlaceholder="ค้นหาพนักงาน..."
              allowClear
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              สถานะ <span className="text-danger-500">*</span>
            </label>
            <select
              aria-label="สถานะ"
              value={formData.is_active ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.value === 'true'}))}
              className="w-full px-3 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white"
            >
              <option value="true">ใช้งาน</option>
              <option value="false">ไม่ใช้งาน</option>
            </select>
          </div>
        </div>

        {/* ประเภทลูกค้า */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1.5">
            ประเภทลูกค้า
          </label>
          <select
            aria-label="ประเภทลูกค้า"
            value={formData.customer_type || ''}
            onChange={(e) => setFormData(prev => ({...prev, customer_type: e.target.value}))}  /* '' = ตั้งใจล้าง → service ส่ง null (undefined = ไม่แตะ) */
            className="w-full px-3 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white"
          >
            <option value="">-- เลือกประเภทลูกค้า --</option>
            <option value="บริษัท รปภ.">บริษัท รปภ.</option>
            <option value="นิติบุคคลหมู่บ้าน">นิติบุคคลหมู่บ้าน</option>
            <option value="นิติบุคคลอาคารชุด">นิติบุคคลอาคารชุด</option>
            <option value="บริษัทเช่าโดยตรง">บริษัทเช่าโดยตรง</option>
            <option value="บุคคลธรรมดา">บุคคลธรรมดา</option>
          </select>
        </div>
      </div>

      {/* Section: การตั้งค่าภาษี */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-warning-600 rounded-md flex items-center justify-center shadow-sm">
            <RiPercentLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">การตั้งค่าภาษี</h3>
        </div>

        <div className="p-4 bg-gradient-to-r from-warning-50 to-warning-100/50 rounded-xl border border-warning-200/50 shadow-sm">
          <label className="block text-sm font-medium text-secondary-700 mb-1.5">
            อัตราหัก ณ ที่จ่าย Default
          </label>
          <select
            aria-label="อัตราหัก ณ ที่จ่าย Default"
            value={formData.default_wht_rate ?? 0}
            onChange={(e) => setFormData(prev => ({...prev, default_wht_rate: Number(e.target.value)}))}
            className="w-full px-3 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white"
          >
            <option value={0}>0% - ไม่หัก ณ ที่จ่าย</option>
            <option value={1}>1% - ค่าขนส่ง</option>
            <option value={2}>2% - ค่าโฆษณา</option>
            <option value={3}>3% - ค่าบริการ</option>
            <option value={5}>5% - ค่าเช่า</option>
          </select>
          <p className="text-xs text-warning-700 mt-2 flex items-center gap-1.5">
            <span className="w-4 h-4 bg-warning-200 rounded-full flex items-center justify-center text-warning-700 text-2xs font-bold">i</span>
            อัตรานี้จะถูกใช้เป็นค่า default เมื่อสร้างใบแจ้งหนี้ใหม่สำหรับบริษัทนี้
          </p>
        </div>
      </div>

      {/* Section: การตั้งค่าวางบิล */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-success-600 rounded-md flex items-center justify-center shadow-sm">
            <RiCalendarCheckLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">การตั้งค่าวางบิล</h3>
        </div>

        <div className="p-4 bg-gradient-to-r from-success-50 to-success-100/50 rounded-xl border border-success-200/50 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                วันออกบิล
              </label>
              <select
                aria-label="วันออกบิล"
                value={formData.billing_day ?? 1}
                onChange={(e) => setFormData(prev => ({...prev, billing_day: Number(e.target.value)}))}
                className="w-full px-3 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>วันที่ {day}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-secondary-500">
                วันที่ใช้ออกบิลในแต่ละเดือน
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                ระยะเวลาชำระเงิน (วัน)
              </label>
              <select
                aria-label="ระยะเวลาชำระเงิน (วัน)"
                value={formData.payment_terms ?? 30}
                onChange={(e) => setFormData(prev => ({...prev, payment_terms: Number(e.target.value)}))}
                className="w-full px-3 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white"
              >
                <option value={5}>5 วัน</option>
                <option value={7}>7 วัน</option>
                <option value={15}>15 วัน</option>
                <option value={30}>30 วัน</option>
                <option value={45}>45 วัน</option>
                <option value={60}>60 วัน</option>
                <option value={90}>90 วัน</option>
              </select>
              <p className="mt-1 text-xs text-secondary-500">
                จำนวนวันที่ให้ชำระหลังออกบิล (ใช้เมื่อไม่ได้ตั้ง "วันครบกำหนดชำระ")
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                วันครบกำหนดชำระ (ของเดือนถัดไป)
              </label>
              <select
                aria-label="วันครบกำหนดชำระ (ของเดือนถัดไป)"
                value={formData.due_day_of_next_month ?? ''}
                onChange={(e) => setFormData(prev => ({...prev, due_day_of_next_month: e.target.value ? Number(e.target.value) : null}))}
                className="w-full px-3 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white"
              >
                <option value="">ไม่กำหนด (ใช้ระยะเวลาชำระเงิน)</option>
                <option value={1}>วันที่ 1</option>
                <option value={5}>วันที่ 5</option>
                <option value={10}>วันที่ 10</option>
                <option value={15}>วันที่ 15</option>
                <option value={20}>วันที่ 20</option>
                <option value={25}>วันที่ 25</option>
              </select>
              <p className="mt-1 text-xs text-secondary-500">
                เช่น "วันที่ 5" = ครบกำหนดชำระวันที่ 5 ของเดือนถัดจากรอบบิล
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section: การตั้งค่ามัดจำ */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-accent-600 rounded-md flex items-center justify-center shadow-sm">
            <RiPercentLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">การตั้งค่ามัดจำ</h3>
        </div>

        <div className="p-4 bg-gradient-to-r from-accent-50 to-accent-100/50 rounded-xl border border-accent-200/50 shadow-sm">
          <ValidatedInput
            label="ยอดค่ามัดจำเก็บสูงสุด (บาท)"
            name="depositAmount"
            type="number"
            min="0"
            step="0.01"
            value={formData.depositSettings?.deposit_amount || 0}
            onKeyDown={blockNonNumericKeys}
            onChange={(e) => !e.target.validity?.badInput && setFormData(prev => ({
              ...prev,
              depositSettings: {
                deposit_amount: parseFloat(e.target.value) || 0
              }
            }))}
            placeholder="40000"
            required
            error={formErrors.depositAmount || formErrors.deposit_amount}
          />

          <p className="text-xs text-accent-600 mt-2 flex items-center gap-1.5">
            <span className="w-4 h-4 bg-accent-200 rounded-full flex items-center justify-center text-accent-700 text-2xs font-bold">i</span>
            เพดานวงเงินมัดจำสะสมของบริษัท (0/ว่าง = ไม่จำกัด)
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyFormFields;
